// Enhanced Quick Actions Panel (like Asana's + button)
import { AppIcon, type IconName } from '@/components/ui/icons';
import React, {  } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useActiveWorkspaceId } from '@/hooks/useActiveWorkspaceId';
import { customerRouteHref } from '@/lib/navigation/canonicalRoutes';
import {
  getAvailableQuickActionIds,
  type QuickActionId,
} from '@/lib/navigation/quickActionAvailability';

interface QuickAction {
  id: string;
  label: string;
  icon: IconName;
  description: string;
  action: () => void;
  shortcut?: string;
  permission?: string;
}

export const QuickActionsPanel: React.FC<{ isOpen: boolean; onClose: () => void }> = ({
  isOpen,
  onClose
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const workspaceId = useActiveWorkspaceId();
  const availableActionIds = React.useMemo(
    () => new Set(getAvailableQuickActionIds(user, workspaceId)),
    [user, workspaceId],
  );

  const quickActions: Array<QuickAction & { id: QuickActionId }> = [
    {
      id: 'new-project',
      label: 'New Project',
      icon: "folderOpen",
      description: 'Create a new project with tasks and milestones',
      action: () => navigate(customerRouteHref(workspaceId, 'projects', { search: { action: 'create' } })),
      shortcut: 'Ctrl+Shift+P',
      permission: 'projects.create'
    },
    {
      id: 'new-task',
      label: 'New Task',
      icon: "checkSquare",
      description: 'Add a task to any project',
      action: () => navigate(customerRouteHref(workspaceId, 'tasks', { search: { action: 'create' } })),
      shortcut: 'Ctrl+Shift+T',
      permission: 'tasks.create'
    },
    {
      id: 'invite-user',
      label: 'Invite User',
      icon: "users",
      description: 'Invite someone to collaborate',
      action: () => navigate(customerRouteHref(workspaceId, 'users', { search: { action: 'invite' } })),
      shortcut: 'Ctrl+Shift+I',
      permission: 'users.invite'
    },
    {
      id: 'schedule-meeting',
      label: 'Schedule Meeting',
      icon: "calendar",
      description: 'Set up a project meeting or review',
      action: () => navigate(customerRouteHref(workspaceId, 'calendar', { search: { action: 'create' } })),
      shortcut: 'Ctrl+Shift+M'
    }
  ];

  const availableActions = quickActions.filter((action) => availableActionIds.has(action.id));

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - transparent click-to-close, no visual overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40"
          />

          {/* Quick Actions Menu */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="fixed top-20 right-6 z-50 w-80 bg-surface-card rounded-2xl [box-shadow:var(--depth-2-shadow)] border border-border-subtle overflow-hidden"
          >
            <div className="p-4 border-b border-border-subtle">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-accent-primary/10 rounded-xl">
                  <AppIcon name="plus" className="w-5 h-5 text-accent-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary">Quick Actions</h3>
                  <p className="text-sm text-text-muted">Create new items instantly</p>
                </div>
              </div>
            </div>

            <div className="p-2 max-h-96 overflow-y-auto">
              {availableActions.map((action) => (
                <motion.button
                  key={action.id}
                  onClick={() => {
                    action.action();
                    onClose();
                  }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full p-3 rounded-lg hover:bg-surface-muted/50 transition-colors text-left group"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-surface-muted rounded-xl group-hover:bg-accent-soft transition-colors">
                      <AppIcon name={action.icon} className="w-4 h-4 text-text-muted group-hover:text-accent-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-text-primary">{action.label}</h4>
                        {action.shortcut && (
                          <span className="text-xs text-text-muted font-mono bg-surface-muted px-2 py-1 rounded-lg">
                            {action.shortcut}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-text-muted mt-1 line-clamp-2">
                        {action.description}
                      </p>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>

            <div className="p-3 bg-surface-muted/50 border-t border-border-subtle">
              <p className="text-xs text-text-muted text-center">
                Press <kbd className="px-1.5 py-0.5 bg-surface-chip rounded-sm text-text-secondary">Esc</kbd> to close
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
